import { isNonProd } from '~/constants/environment';
import isServerSideRendering from '~/utilities/isServerSideRendering';
import { saveItemToLocalStorage } from '~/utilities/localStorage';
import safeParse from '~/utilities/safeParse';
import { eventTimes, localStorageKey } from './constants';
import type {
  EventListener,
  EventListeners,
  EventName,
  EventTimes
} from './types';

export enum Scope {
  all,
  local,
  remote
}

function isEventName(eventName: string): eventName is EventName {
  return Object.keys(eventTimes).includes(eventName);
}

export default class EventManager {
  enableCrossTabFunctionality: boolean;

  eventTimes: EventTimes = { ...eventTimes };

  listeners: EventListeners = {
    availability: [],
    favoriteAdd: [],
    favoriteRemove: [],
    logIn: [],
    logOut: []
  };

  constructor(enableCrossTabFunctionality: boolean) {
    this.enableCrossTabFunctionality = enableCrossTabFunctionality;

    if (this.enableCrossTabFunctionality && !isServerSideRendering) {
      window.addEventListener('storage', this.listen.bind(this));
    }
  }

  listen({ key, newValue }: StorageEvent) {
    if (key !== localStorageKey) return;

    const events = newValue
      ? safeParse<EventTimes>(newValue) ?? eventTimes
      : eventTimes;

    Object.entries(events).forEach(([eventName, whenToUpdate]) => {
      if (!isEventName(eventName)) return;

      if (this.eventTimes[eventName] !== whenToUpdate) {
        if (isNonProd) {
          console.debug('Managed event arrived', {
            eventName,
            whenToUpdate: new Date(whenToUpdate)
          });
        }

        this.eventTimes[eventName] = whenToUpdate;

        this.listeners[eventName].forEach((listener: EventListener) =>
          listener(this.eventTimes[eventName])
        );
      }
    });
  }

  on(eventName: EventName, listener: EventListener) {
    this.listeners[eventName] = this.listeners[eventName].concat(listener);
  }

  off(eventName: EventName, listener: EventListener) {
    this.listeners[eventName] = this.listeners[eventName].filter(
      fn => fn !== listener
    );
  }

  trigger(
    eventName: EventName,
    offset: number = 0,
    scope: Scope = Scope.local
  ) {
    this.eventTimes[eventName] = Date.now() + offset;

    if ([Scope.local, Scope.all].includes(scope)) {
      this.listeners[eventName].forEach(listener =>
        listener(this.eventTimes[eventName])
      );
    }

    if (
      this.enableCrossTabFunctionality &&
      [Scope.remote, Scope.all].includes(scope)
    ) {
      if (isNonProd) {
        console.debug('Managed event triggered', { eventName, scope });
      }

      saveItemToLocalStorage(localStorageKey, this.eventTimes);
    }
  }

  cancelPurchase() {
    this.trigger('availability', 500, Scope.all);
  }

  favoriteAdd() {
    this.trigger('favoriteAdd', 0, Scope.all);
  }

  favoriteRemove() {
    this.trigger('favoriteRemove', 0, Scope.all);
  }

  logIn() {
    this.trigger('logIn', 500, Scope.remote);
  }

  logOut() {
    this.trigger('logOut', 5000, Scope.remote);
  }

  preOrder() {
    this.trigger('availability', 500, Scope.all);
  }

  purchase() {
    this.trigger('availability', 15000, Scope.all);
  }
}
